import { Link, useParams, useSearchParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Archive, ChevronLeft, Globe, KeyRound, ScrollText, Settings2 } from 'lucide-react';
import { PageHeader, Skeleton, StatusPill, TabPanel, Tabs } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { ServerCopies } from '@/components/nodes/ServerCopies';
import { ServerJournal } from '@/components/nodes/ServerJournal';
import { ServerKind, ServerOverview } from '@/components/nodes/ServerOverview';
import { ServerSites } from '@/components/nodes/ServerSites';
import { CredentialsTab } from '@/components/sites/CredentialsTab';
import { useRoles } from '@/hooks/useAuth';
import { useNode } from '@/hooks/useNodes';
import { NODE_STATE } from '@/utils/labels';
import { nodeState } from '@/utils/nodes';
import { can } from '@/utils/permissions';

type Tab = 'overview' | 'sites' | 'backups' | 'credentials' | 'activity';
const TABS: readonly Tab[] = ['overview', 'sites', 'backups', 'credentials', 'activity'];

function BackLink(): ReactNode {
  return (
    <Link to="/servers" className="back-link">
      <ChevronLeft size={14} aria-hidden="true" />К списку серверов
    </Link>
  );
}

/**
 * One server, in full (ADR-0058): what the machine is and how full, the sites
 * on it, their copies, the ways into it kept for people, and what the trail
 * says about it. The tab is in the address, so a link opens where it points.
 */
export function ServerPage(): ReactNode {
  const { id = '' } = useParams();
  const roles = useRoles();
  const [params, setParams] = useSearchParams();
  const node = useNode(id);

  const mayCredentials = can(roles, 'secrets.manage');
  const mayAudit = can(roles, 'audit.read');
  const asked = params.get('tab') as Tab | null;
  const tab: Tab = asked && TABS.includes(asked) && (asked !== 'credentials' || mayCredentials) && (asked !== 'activity' || mayAudit) ? asked : 'overview';
  const open = (next: Tab) =>
    setParams(
      (current) => {
        const copy = new URLSearchParams(current);
        if (next === 'overview') copy.delete('tab');
        else copy.set('tab', next);
        return copy;
      },
      { replace: true },
    );

  if (node.isPending) {
    return (
      <div className="page">
        <BackLink />
        <Skeleton height={32} width="40%" />
        <Skeleton height={240} />
      </div>
    );
  }
  if (node.isError) {
    return (
      <div className="page">
        <BackLink />
        <ErrorState error={node.error} fallback="Сервер не найден" />
      </div>
    );
  }

  const data = node.data;
  const host = data.role === 'host';
  const sites = host ? data.hostedSiteCount : data.siteCount;

  return (
    <div className="page">
      <div>
        <BackLink />
        <PageHeader title={data.name} subtitle={host ? 'Внешний сервер: панель следит за его сайтами и делает их копии' : 'Сервер панели: на нём работают сайты панели'} />
        <div className="row-tight row-wrap" style={{ marginTop: 'var(--space-3)' }}>
          <StatusPill map={NODE_STATE} status={nodeState(data)} />
          <span className="tag">
            <ServerKind node={data} />
          </span>
          {data.ipv4 ? <span className="tag mono">{data.ipv4}</span> : null}
          {data.agentVersion ? <span className="tag mono">агент {data.agentVersion}</span> : null}
        </div>
      </div>

      <Tabs
        label="Разделы сервера"
        active={tab}
        onChange={open}
        tabs={[
          { id: 'overview', label: 'Обзор', icon: Settings2 },
          { id: 'sites', label: 'Сайты', icon: Globe, count: sites },
          { id: 'backups', label: 'Копии', icon: Archive },
          ...(mayCredentials ? [{ id: 'credentials' as const, label: 'Доступы', icon: KeyRound }] : []),
          ...(mayAudit ? [{ id: 'activity' as const, label: 'Журнал', icon: ScrollText }] : []),
        ]}
      />

      <TabPanel id="overview" active={tab}>
        <ServerOverview node={data} />
      </TabPanel>
      <TabPanel id="sites" active={tab}>
        <ServerSites node={data} />
      </TabPanel>
      <TabPanel id="backups" active={tab}>
        <ServerCopies nodeId={data.id} />
      </TabPanel>
      {mayCredentials ? (
        <TabPanel id="credentials" active={tab}>
          <CredentialsTab owner={{ kind: 'node', id: data.id }} />
        </TabPanel>
      ) : null}
      {mayAudit ? (
        <TabPanel id="activity" active={tab}>
          <ServerJournal nodeId={data.id} />
        </TabPanel>
      ) : null}
    </div>
  );
}
